/**
 * Settings page — view account email + change-email flow with OTP verification
 * of the new address. Prototype source: data-page="settings".
 * Local state only; BFF/Firebase wiring in Phase 4. British English.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TopNav } from '../components/TopNav';
import { OtpInput } from '../components/OtpInput';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { useSession } from '../context/SessionContext';
import { signOut } from '../lib/useAuth';
import { api, downloadBlob } from '../lib/api';
import { reopenConsentBanner } from '../components/ConsentBanner';
import './Settings.css';

type Step = 'view' | 'step1' | 'step2' | 'step3' | 'step4';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DELETE_CONFIRM = 'DELETE';

export function Settings() {
  const navigate = useNavigate();
  const session = useSession();
  const currentEmail = session.email ?? 'you@example.com';

  const [step, setStep] = useState<Step>('view');
  const [newEmail, setNewEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [otpError, setOtpError] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  // Delete-account danger zone state.
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(false);

  // Data-export state (UK GDPR Art. 15/20 — right of access / portability).
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(false);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2800);
  };

  const closeFlow = () => {
    setStep('view');
    setOtp('');
    setNewEmail('');
    setEmailError('');
    setOtpError(false);
  };

  const handleSendCurrentCode = () => {
    setLoading(true);
    // BFF: send OTP to current address.
    api
      .post('/api/v1/auth/send-otp', { email: currentEmail })
      .then(() => {
        setLoading(false);
        setStep('step2');
        setOtp('');
      })
      .catch(() => {
        setLoading(false);
        setStep('step2'); // Proceed anyway — generic success.
        setOtp('');
      });
  };

  const handleVerifyCurrent = () => {
    if (otp.length !== 6) return;
    // BFF: verify the OTP for the current address.
    api
      .post('/api/v1/auth/verify-otp', { email: currentEmail, code: otp })
      .then(() => {
        setLoading(false);
        setStep('step3');
        setOtp('');
      })
      .catch(() => {
        setLoading(false);
        setOtpError(true);
      });
  };

  const handleSendNewCode = () => {
    const v = newEmail.trim();
    if (!EMAIL_RE.test(v)) {
      setEmailError('Enter a valid email address.');
      return;
    }
    if (v.toLowerCase() === currentEmail.toLowerCase()) {
      setEmailError("That's already your email address.");
      return;
    }
    setLoading(true);
    // BFF: send OTP to the new address (allow-list checked).
    api
      .post('/api/v1/auth/send-otp', { email: v })
      .then(() => {
        setLoading(false);
        setStep('step4');
        setOtp('');
      })
      .catch(() => {
        setLoading(false);
        setStep('step4'); // Proceed anyway — generic success.
        setOtp('');
      });
  };

  const handleConfirmNew = () => {
    if (otp.length !== 6) return;
    setLoading(true);
    // BFF: verify the OTP for the new address, then update the Firebase account email.
    api
      .post('/api/v1/auth/verify-otp', { email: newEmail, code: otp })
      .then(() => {
        // Phase 4: Admin SDK updateEmail call will be added to a dedicated
        // /api/v1/settings/change-email endpoint. For now, update the session.
        setLoading(false);
        session.setEmail(newEmail);
        closeFlow();
        showToast('Email address updated');
      })
      .catch(() => {
        setLoading(false);
        setOtpError(true);
      });
  };

  // UK GDPR Art. 17 — right to erasure. Typed confirmation ("DELETE") prevents
  // accidental deletion. On success the Firebase session is cleared and the
  // user is redirected to a public goodbye page.
  const handleDeleteAccount = () => {
    if (deleteConfirm !== DELETE_CONFIRM) return;
    setDeleting(true);
    setDeleteError(false);
    api
      .delete('/api/v1/me')
      .then(async () => {
        // Sign out of Firebase + clear the session, then redirect.
        try {
          await signOut();
        } catch {
          // The Auth user may already be deleted by the BFF — ignore.
        }
        session.signOut();
        navigate('/goodbye', { replace: true });
      })
      .catch(() => {
        setDeleting(false);
        setDeleteError(true);
      });
  };

  // UK GDPR Art. 15/20 — download a JSON export of all the user's personal data.
  // Triggers a browser download via a Blob URL.
  const handleDownloadData = () => {
    setExporting(true);
    setExportError(false);
    downloadBlob('/api/v1/me/export')
      .then(({ blob, filename }) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename ?? 'idea-toolkit-data.json';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setExporting(false);
        showToast('Your data is downloading');
      })
      .catch(() => {
        setExporting(false);
        setExportError(true);
      });
  };

  return (
    <>
      <TopNav title="Settings" onBack={() => navigate('/')} />

      <div className="itk-wrap itk-wrap--narrow">
        <div className="itk-page-head">
          <div className="itk-overline">Account</div>
          <h1 className="itk-page-title">Settings</h1>
          <p className="itk-sec-desc">Manage the email address you use to sign in, and your account.</p>
        </div>

        <section className="itk-section-box" aria-labelledby="cookieTitle">
          <div className="itk-overline">Cookies</div>
          <h2 className="itk-sec-title" id="cookieTitle">
            Cookie settings
          </h2>
          <p className="itk-sec-desc" style={{ marginBottom: 18 }}>
            Change your cookie preferences at any time. You can also read our{' '}
            <a href="/cookie-policy">cookie policy</a>.
          </p>
          <Button variant="secondary" onClick={reopenConsentBanner}>
            Cookie settings
          </Button>
        </section>

        <section className="itk-section-box" aria-labelledby="emTitle">
          <div className="itk-overline">Email address</div>
          <h2 className="itk-sec-title" id="emTitle">
            Your email
          </h2>
          <p className="itk-sec-desc" style={{ marginBottom: 18 }}>
            This is the address we use to sign you in and send your alerts.
          </p>

          {step === 'view' && (
            <div className="itk-value-row">
              <div>
                <div className="itk-value-label">Current email address</div>
                <div className="itk-value">{currentEmail}</div>
              </div>
              <Button variant="secondary" onClick={() => setStep('step1')}>
                Change email address
              </Button>
            </div>
          )}

          {step === 'step1' && (
            <div className="itk-flow">
              <div className="itk-flow-progress">Step 1 of 4</div>
              <div className="itk-flow-title">Confirm it&rsquo;s you</div>
              <p className="itk-flow-sub">
                We&rsquo;ll send a 6-digit verification code to your current email address,{' '}
                <b>{currentEmail}</b>.
              </p>
              <div className="itk-flow-actions">
                <Button loading={loading} onClick={handleSendCurrentCode}>
                  Send code
                </Button>
                <Button variant="secondary" onClick={closeFlow}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {step === 'step2' && (
            <div className="itk-flow">
              <div className="itk-flow-progress">Step 2 of 4</div>
              <div className="itk-flow-title">Enter your code</div>
              <p className="itk-flow-sub">
                We sent a 6-digit code to <b>{currentEmail}</b>.
              </p>
              <OtpInput
                value={otp}
                onChange={(v) => {
                  setOtp(v);
                  setOtpError(false);
                }}
                error={otpError}
              />
              {otpError && (
                <p className="itk-field-err itk-field-err--show">That code isn&rsquo;t right. Check it and try again.</p>
              )}
              <div className="itk-flow-actions">
                <Button loading={loading} onClick={handleVerifyCurrent} disabled={otp.length !== 6}>
                  Verify
                </Button>
                <Button variant="secondary" onClick={closeFlow}>
                  Cancel
                </Button>
              </div>
              <div className="itk-help">Not seeing it? Check your spam folder.</div>
            </div>
          )}

          {step === 'step3' && (
            <div className="itk-flow">
              <div className="itk-flow-progress">Step 3 of 4</div>
              <div className="itk-flow-title">New email address</div>
              <p className="itk-flow-sub">
                Enter the address you&rsquo;d like to use. We&rsquo;ll send a code there to confirm it.
              </p>
              <Input
                label="New email address"
                type="email"
                placeholder="you@newaddress.com"
                value={newEmail}
                onChange={(e) => {
                  setNewEmail(e.target.value);
                  setEmailError('');
                }}
                error={emailError}
              />
              <div className="itk-flow-actions">
                <Button loading={loading} onClick={handleSendNewCode}>
                  Send code
                </Button>
                <Button variant="secondary" onClick={closeFlow}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {step === 'step4' && (
            <div className="itk-flow">
              <div className="itk-flow-progress">Step 4 of 4</div>
              <div className="itk-flow-title">Confirm your new address</div>
              <p className="itk-flow-sub">
                We sent a 6-digit code to <b>{newEmail}</b>.
              </p>
              <OtpInput
                value={otp}
                onChange={(v) => {
                  setOtp(v);
                  setOtpError(false);
                }}
                error={otpError}
              />
              {otpError && (
                <p className="itk-field-err itk-field-err--show">That code isn&rsquo;t right. Check it and try again.</p>
              )}
              <div className="itk-flow-actions">
                <Button loading={loading} onClick={handleConfirmNew} disabled={otp.length !== 6}>
                  Confirm new email
                </Button>
                <Button variant="secondary" onClick={closeFlow}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </section>

        {/* UK GDPR Art. 15/20 — right of access / portability. Download a JSON
            export of all the personal data we hold about the user. */}
        <section className="itk-section-box" aria-labelledby="expTitle">
          <div className="itk-overline">Your data</div>
          <h2 className="itk-sec-title" id="expTitle">
            Download your data
          </h2>
          <p className="itk-sec-desc" style={{ marginBottom: 18 }}>
            Download a copy of all the personal data we hold about you — your profile, ideas,
            agreements, bookings, learning progress and uploaded file list — as a single JSON file.
            Confidentiality agreement PDFs are available separately via the Book page.
          </p>
          {exportError && (
            <p className="itk-field-err itk-field-err--show" style={{ marginBottom: 12 }}>
              We couldn&rsquo;t prepare your export right now. Please try again or contact us.
            </p>
          )}
          <Button variant="secondary" loading={exporting} onClick={handleDownloadData} disabled={exporting}>
            Download my data
          </Button>
        </section>

        {/* UK GDPR Art. 17 — right to erasure. Danger zone. */}
        <section className="itk-section-box itk-danger-zone" aria-labelledby="delTitle">
          <div className="itk-overline itk-overline--danger">Danger zone</div>
          <h2 className="itk-sec-title" id="delTitle">
            Delete my account
          </h2>
          <p className="itk-sec-desc" style={{ marginBottom: 18 }}>
            Permanently delete your account and all the data we hold about you. This includes your
            ideas, uploaded documents, bookings and profile. Any signed confidentiality agreements are
            retained for legal reasons (anonymised, 15 years). This cannot be undone.
          </p>
          <Input
            label='Type "DELETE" to confirm'
            type="text"
            placeholder="DELETE"
            value={deleteConfirm}
            onChange={(e) => {
              setDeleteConfirm(e.target.value);
              setDeleteError(false);
            }}
            error={deleteError ? 'We couldn&rsquo;t delete your account right now. Please try again or contact us.' : undefined}
          />
          <div className="itk-flow-actions" style={{ marginTop: 16 }}>
            <Button
              variant="danger"
              loading={deleting}
              onClick={handleDeleteAccount}
              disabled={deleteConfirm !== DELETE_CONFIRM || deleting}
            >
              Delete my account
            </Button>
          </div>
        </section>
      </div>

      {toast && (
        <div className="itk-toast itk-toast--show" role="status" aria-live="polite">
          {toast}
        </div>
      )}
    </>
  );
}